/**
 * Brand-scope registry.
 *
 * Single source of truth for every piece of homepage copy that names the
 * geographic extent of the trail. The site evolves through a handful of
 * named stages rather than silently auto-generating strings from data,
 * so each promotion is a deliberate editorial decision with pre-written,
 * atmospheric copy.
 *
 * --- How to promote a stage ---
 *
 *   1. Add the new state(s) to `REGIONS` in `src/lib/regions.ts`, populate
 *      `CHEESERIES` / `ACCOMMODATIONS`, and add a `STATE_HUBS` entry in
 *      `src/lib/trailHubs.ts`. Also wire a `<Route>` in `src/main.tsx`.
 *   2. Flip `BRAND_STAGE` below to the next stage key.
 *   3. Update `index.html` SEO metadata by hand — it is intentionally
 *      static so search engines and social scrapers see a canonical,
 *      hand-tuned scope. Lines to update (search for "Vermont & Maine"):
 *         - <title>
 *         - <meta name="description">
 *         - <meta name="keywords">
 *         - <meta property="og:title"> / og:description
 *         - <meta name="twitter:title"> / twitter:description
 *         - JSON-LD WebSite.description
 *         - JSON-LD TouristTrip.name / description
 *         - FAQPage answer texts that mention "Vermont and Maine"
 *      Use the matching `STAGES[...]` entry below as the source.
 *   4. Update `public/sitemap.xml` with new state-hub URLs.
 *   5. Review `src/lib/faq.ts` — the "Do you cover NH/MA/NY?" answer and
 *      any question text that currently reads "today the focus is…" will
 *      need a short rewrite so the FAQ matches the new scope.
 *   6. Review README.md for any stage-specific language.
 */

export type BrandStage =
  | 'vt-me'                   // Current: Vermont & Maine only
  | 'northern-new-england'    // After NH is added
  | 'new-england'             // After MA is added
  | 'new-england-northeast';  // After NY (Finger Lakes etc.)

export interface ScopeCopy {
  /** Short label for pills, sidebar subtitle, map corner chip. */
  label: string;
  /** Inline title form, used inside headings mid-sentence. */
  title: string;
  /** H2 on the homepage editorial section. */
  editorialTitle: string;
  /** One-sentence hero subhead under the H1. */
  heroTagline: string;
  /** H2 introducing the regions section. */
  regionsTitle: string;
  /** Short positioning line, e.g. for footers / compact UI. */
  projectTagline: string;
  /** <title> for the homepage. Keep under ~60 chars for SERP display. */
  metaTitle: string;
  /** <meta name="description"> for the homepage. ~150 chars is ideal. */
  metaDescription: string;
}

const STAGES: Record<BrandStage, ScopeCopy> = {
  'vt-me': {
    label: 'Vermont · Maine',
    title: 'Vermont & Maine',
    editorialTitle: 'The Vermont & Maine Cheese Trail',
    heroTagline:
      'A curated guide to the Vermont & Maine artisan cheese trail. Discover real creameries, thoughtful places to stay, and plot your own tasting pilgrimage.',
    regionsTitle: 'Cheesemaking regions on the trail',
    projectTagline: 'Cheese trails and tasting weekends across Vermont and Maine.',
    metaTitle: 'The Curd Compass — Vermont & Maine Cheese Trail',
    metaDescription:
      'A curated guide to the Vermont & Maine artisan cheese trail. Plan a tasting route through real farmstead creameries, country inns, and places to stay — all on an interactive map.',
  },

  'northern-new-england': {
    label: 'Northern New England',
    title: 'Northern New England',
    editorialTitle: 'The Northern New England Cheese Trail',
    heroTagline:
      'A curated guide to the cheese trails of Northern New England. Vermont cellars, Maine coast dairies, and the quiet New Hampshire connector — mapped as one weekend-sized guide.',
    regionsTitle: 'Cheesemaking regions on the trail',
    projectTagline: 'Cheese trails and tasting weekends across Northern New England.',
    metaTitle: 'The Curd Compass — Northern New England Cheese Trail',
    metaDescription:
      'A curated guide to the cheese trails of Vermont, Maine, and New Hampshire. Plan a tasting route through real farmstead creameries, country inns, and places to stay.',
  },

  'new-england': {
    label: 'New England',
    title: 'New England',
    editorialTitle: 'The New England Cheese Trail',
    heroTagline:
      'New England cheese trails, farm stays, and tasting weekends — from Vermont cellars to the Berkshires to the midcoast Maine harbors.',
    regionsTitle: 'Cheesemaking regions on the trail',
    projectTagline: 'Cheese trails, farm stays, and tasting weekends across New England.',
    metaTitle: 'The Curd Compass — New England Cheese Trails',
    metaDescription:
      'A curated guide to the artisan cheese trails of New England. Vermont, Maine, New Hampshire, and Massachusetts — creameries, farm stays, and scenic weekend routes on one interactive map.',
  },

  'new-england-northeast': {
    label: 'New England · Northeast',
    title: 'New England & the Northeast',
    editorialTitle: 'Cheese trails of New England and the Northeast',
    heroTagline:
      'Cheese trails, farm stays, and tasting weekends across New England and the Northeast — Vermont cellars, Finger Lakes wineries, Maine harbors, and the routes that stitch them together.',
    regionsTitle: 'Cheesemaking regions on the trail',
    projectTagline: 'Cheese trails, farm stays, and tasting weekends across New England and the Northeast.',
    metaTitle: 'The Curd Compass — New England & Northeast Cheese Trails',
    metaDescription:
      'A curated guide to artisan cheese trails across New England and the Northeast. Vermont, Maine, New Hampshire, Massachusetts, and the Finger Lakes — mapped as weekend-sized itineraries.',
  },
};

/**
 * The currently-published stage. Bump this when promoting the brand scope.
 * All homepage copy that names the trail's geographic extent reads from
 * `SCOPE` below, so changing this one line updates the sidebar, hero,
 * editorial headings, and footer in lockstep.
 */
export const BRAND_STAGE: BrandStage = 'vt-me';

export const SCOPE: ScopeCopy = STAGES[BRAND_STAGE];
